import React, { useEffect } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import ConnectionsList from '../../Components/Lists/ConnectionsList';
import { GET_COMPANY_CONNECTIONS } from '../../graphql/queries';
import { DELETE_COMPANY_CONNECTION } from '../../graphql/mutations';
import { useQuery, useMutation } from '@apollo/client';
import { evictConnectionScopedData } from '../../graphql/cacheUtils';
import ConnectionCardSkeleton from '../../Components/Skeletons/ConnectionCardSkeleton';
import EmptyState from '../../Components/EmptyState/EmptyState';
import { toast } from 'react-toastify';

const MyConnections: React.FC = () => {
  const emptyImage1 = require("../../assets/images/Empty1.png");
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, loading, error, refetch } = useQuery(GET_COMPANY_CONNECTIONS);

  // Handle error from OAuth redirect
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      toast.error(decodeURIComponent(errorParam));
      // Remove the error param from URL
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [deleteConnection] = useMutation(DELETE_COMPANY_CONNECTION, {
    // Orders, listings and other connection-scoped data are stale once the
    // connection is gone — evict them so those pages refetch instead of
    // showing cached results from the removed store.
    update: (cache) => evictConnectionScopedData(cache),
  });

  const handleDeleteConnection = async (shopDomain: string) => {
    try {
      const response = await deleteConnection({
        variables: { shopDomain },
      });

      if (response.data.deleteCompanyConnection) {
        // Refetch connections after deletion
        await refetch();
      } else {
        toast.error(response.data.deleteCompanyConnection.message || "Failed to delete connection.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("An error occurred while deleting the connection.");
    }
  };

  return (
    <Container>
      <Box
        my={4}
        sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h4" gutterBottom>
          My Connections
        </Typography>
      </Box>

      {loading && <ConnectionCardSkeleton />}

      {error && (
        <Typography color="error" data-testid="error-message">
          Error: Failed to load company connections.
        </Typography>
      )}

      {/* Display Connections List */}
      {!loading && !error && data?.companyConnections?.length > 0 && (
        <ConnectionsList
          connections={data.companyConnections}
          onDelete={handleDeleteConnection}
        />
      )}

      {/* Empty Placeholder */}
      {!loading && data?.companyConnections?.length === 0 && (
        <EmptyState
          image={emptyImage1}
          text="You don't have a Shopify store connected yet. Install the ShareStock app from your Shopify admin to get started."
          actionButtons={<></>}
        />
      )}

    </Container>
  );
};

export default MyConnections;
